import {Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react';

/**
 * Set the initial selected items (if any), then watch it for changes and
 * update the state accordingly.
 *
 * usable in lists and tables
 *
 */
export const useSelection = <T extends string>(
  userSelectedItems: T[] | undefined,
  controlledSelection: boolean,
  onSelectedItemsChange?: (selectedItems: T[]) => void
): {
  selectedItemIds: Set<T>;
  selectedItemsIdsAsString: string;
  setSelectedItemIds: Dispatch<SetStateAction<Set<T>>>;
} => {
  const [selectedItemIds, setSelectedItemIds] = useState(
    new Set<T>(userSelectedItems)
  );

  /** Comma-separated list of user selected IDs, used to avoid useEffect loops. */
  const userSelectedRowIdsAsString = useMemo(
    () => [...(userSelectedItems || [])].join(','),
    [userSelectedItems]
  );

  useEffect(
    () => {
      if (!controlledSelection) {
        setSelectedItemIds(new Set(userSelectedItems));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userSelectedRowIdsAsString]
  );

  /** Comma-separated list of selected IDs, used to avoid useEffect loops. */
  const selectedItemsIdsAsString = useMemo(
    () => [...selectedItemIds].join(','),
    [selectedItemIds]
  );

  // For some reason this state changes values without the setter being called in certain situations
  // memoizing the state fixes this behavior. This fix was made for LEXIAN-6299, the reason for the state change
  // without set being called was not found. So if you remove this memo, please check if the case can be reproduced
  const memoizedSelectionSet = useMemo(
    () => selectedItemIds,
    // we do only want to react to changed values in the set not the changed set itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemsIdsAsString]
  );

  useEffect(
    () => {
      if (!controlledSelection) {
        onSelectedItemsChange?.([...selectedItemIds]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemsIdsAsString]
  );

  if (controlledSelection) {
    return {
      selectedItemIds: new Set<T>(userSelectedItems),
      selectedItemsIdsAsString: userSelectedItems?.join(',') || '',
      setSelectedItemIds: (
        itemsSet: Set<T> | ((existingState: Set<T>) => Set<T>)
      ) => {
        onSelectedItemsChange?.(
          Array.from(
            typeof itemsSet === 'function'
              ? itemsSet(memoizedSelectionSet).values()
              : itemsSet.values()
          )
        );
      },
    };
  } else {
    return {
      selectedItemIds: memoizedSelectionSet,
      selectedItemsIdsAsString,
      setSelectedItemIds,
    };
  }
};

/** Removes or adds a string from/to an array of strings depending if it is already inside or not*/
export const toggleItemInList = (list: string[], item: string): string[] => {
  const localList = [...list];
  const idx = localList.indexOf(item);
  if (idx === -1) {
    return [...localList, item];
  }
  localList.splice(idx, 1);
  return localList;
};
